// src/components/AdminMain.jsx
import React, { useEffect, useState, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FaLanguage } from 'react-icons/fa'
import { CircularProgress, Box, Typography, alpha } from '@mui/material'
import { styled } from '@mui/material/styles'

// ============================================
// NEW LOADING COMPONENT - FIXED
// ============================================
const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  width: '100%',
  background: `linear-gradient(135deg, ${alpha(theme?.palette?.primary?.main || '#1976d2', 0.05)} 0%, ${alpha(theme?.palette?.primary?.light || '#42a5f5', 0.1)} 100%)`,
  gap: '24px',
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme?.palette?.primary?.main || '#1976d2',
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  },
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme?.palette?.text?.secondary || '#666',
  animation: 'pulse 1.5s ease-in-out infinite',
  '@keyframes pulse': {
    '0%': { opacity: 0.6 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.6 },
  },
}));

const LoadingDots = styled(Box)({
  display: 'flex',
  gap: '8px',
  '& span': {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#1976d2',
    animation: 'bounce 1.4s ease-in-out infinite both',
  },
  '& span:nth-of-type(1)': { animationDelay: '-0.32s' },
  '& span:nth-of-type(2)': { animationDelay: '-0.16s' },
  '& span:nth-of-type(3)': { animationDelay: '0s' },
  '@keyframes bounce': {
    '0%, 80%, 100%': { transform: 'scale(0)', opacity: 0.4 },
    '40%': { transform: 'scale(1)', opacity: 1 },
  },
});

const Loading = () => {
  return (
    <LoadingContainer>
      <StyledCircularProgress size={60} thickness={4} />
      <LoadingText variant="h6">
        Loading...
      </LoadingText>
      <LoadingDots>
        <span />
        <span />
        <span />
      </LoadingDots>
    </LoadingContainer>
  );
};

// Lazy load all route components for code splitting and performance optimization
const Dashboard = React.lazy(() => import('./Dashboard/Dashboard'))
const Home = React.lazy(() => import('../Home'))
const LoginPage = React.lazy(() => import('../LoginPage'))
const All = React.lazy(() => import('./Dashboard/All'))
const Profile = React.lazy(() => import('./Profile/Profile'))
const Editprofile = React.lazy(() => import('./Profile/Editprofile'))
const Pharmacy = React.lazy(() => import('./Pharmacy/Pharmacy'))
const Purchase = React.lazy(() => import('./Pharmacy/Purchase'))
const Sales = React.lazy(() => import('./Pharmacy/Sales'))
const Expired = React.lazy(() => import('./Pharmacy/Expired'))
const Inventory = React.lazy(() => import('./Pharmacy/Inventory'))
const Salesviewall = React.lazy(() => import('./Pharmacy/Salesviewall'))
const Addmedicine = React.lazy(() => import('./Pharmacy/Addmedicine'))
const Addpatients = React.lazy(() => import('./Patients/Addpatients'))
const Patientrecord = React.lazy(() => import('./Patients/Patientrecord'))
const Addusers = React.lazy(() => import('./Patients/Addusers'))
const Results = React.lazy(() => import('./Lab/Results'))
const PublishResult = React.lazy(() => import('./Lab/PublishResult'))
const Addservices = React.lazy(() => import('./Lab/Addservices'))
const AddveiwAll = React.lazy(() => import('./Lab/AddveiwAll'))
const Addpackages = React.lazy(() => import('./Lab/Addpackages'))
const PrintResult = React.lazy(() => import('./Lab/PrintResult'))
const AddDeparment = React.lazy(() => import('./Department/AddDeparment'))
const AddDoctor = React.lazy(() => import('./Doctor/AddDoctor'))
const DoctorHome = React.lazy(() => import('../DoctorFrame/DoctorHome'))
const DocMypatients = React.lazy(() => import('../DoctorFrame/DocMypatients'))
const DocDiagnosis = React.lazy(() => import('../DoctorFrame/DocDiagnosis'))
const Signup = React.lazy(() => import('../Signup'))
const PatientFrameHome = React.lazy(() => import('../PatientFrame/PatientFrameHome'))
const PatientBooking = React.lazy(() => import('../PatientFrame/PatientBooking'))
const LabHome = React.lazy(() => import('../LabFrame/LabHome'))
const PharmacyHome = React.lazy(() => import('../PharmacyFrame/PharmacyHome'))
const ReceptionHome = React.lazy(() => import('../ReceptionFrame/ReceptionHome'))
const Viewdetails = React.lazy(() => import('./Pharmacy/Viewdetails'))
const Addfloor_room = React.lazy(() => import('./Addfloor/Addfloor_room'))
const Myschedule = React.lazy(() => import('../PatientFrame/Myschedule'))
const Discharge = React.lazy(() => import('../Shared/Discharge'))
const Monitorpatient = React.lazy(() => import('./Patients/Monitorpatient'))
const AdminDashboard = React.lazy(() => import('./AdminDashboard'))
const Doctordashboard = React.lazy(() => import('../DoctorFrame/Doctordashboard'))
const Consultedpatients = React.lazy(() => import('../DoctorFrame/Consultedpatients'))
const PatientDashboard = React.lazy(() => import('../PatientFrame/PatientDashboard'))
const Adminresultchart = React.lazy(() => import('./Charts/Adminresultchart'))
const AdminDashChart = React.lazy(() => import('./Charts/AdminDashChart'))
const AvailableDoctor = React.lazy(() => import('./Doctor/AvailableDoctor'))
const AddPharmacyPatients = React.lazy(() => import('./Pharmacy/AddPharmacyPatients'))
const PharmacyDashboard = React.lazy(() => import('../PharmacyFrame/PharmacyDashboard'))
const PharmacyDetails = React.lazy(() => import('../PharmacyFrame/PharmacyDetails'))
const ReceptionDashboard = React.lazy(() => import('../ReceptionFrame/ReceptionDashboard'))
const LabDashboard = React.lazy(() => import('../LabFrame/LabDashboard'))
const AssignRole = React.lazy(() => import('../AdminFrame/Settings/AssignRole/AssignRole'))
const NurseHome = React.lazy(() => import('../NurseFrame/NurseHome'))
const NurseNavpad = React.lazy(() => import('../NurseFrame/NurseNavpad'))
const NurseDasboard = React.lazy(() => import('../NurseFrame/Nursedashboard'))
const NurseMypatients = React.lazy(() => import('../NurseFrame/NurseMypatient'))
const NurseInpatient = React.lazy(() => import('../NurseFrame/NurseInpatient'))
const DocINpatients = React.lazy(() => import('../DoctorFrame/DocINpatients'))
const Pychomatric = React.lazy(() => import('./Dashboard/Pychomatric'))
const Errorhandling = React.lazy(() => import('./Errorhandling'))
const AddArticle = React.lazy(() => import('../DoctorFrame/AddArticle'))
const AddTemplate = React.lazy(() => import('./Doctor/AddTemplate'))
const RequestDonation = React.lazy(() => import('./BloodBank/RequestDonation'))
const BloodDonation = React.lazy(() => import('./BloodBank/BloodDonation'))
const SignalR = React.lazy(() => import('../DoctorFrame/SignalR'))
const Vedioconference = React.lazy(() => import('../DoctorFrame/VedioConference'))
const VedioCreateRoom = React.lazy(() => import('../DoctorFrame/VedioCreateRoom'))
const Mycalender = React.lazy(() => import('../DoctorFrame/Mycalender'))
const Invoice = React.lazy(() => import('./Invoices/Invoice'))
const Printinvoice = React.lazy(() => import('./Invoices/Printinvoice'))
const Labinvoice = React.lazy(() => import('./Invoices/Labinvoice'))
const Searchinvoices = React.lazy(() => import('./Invoices/Searchinvoices'))
const InvoiceParent = React.lazy(() => import('./Invoices/InvoiceParent'))
const Addservicegroup = React.lazy(() => import('./Lab/Addservicegroup'))
const Addspecimen = React.lazy(() => import('./Lab/Addspecimen'))
const PatientLabBooking = React.lazy(() => import('../PatientFrame/PatientLabBooking'))
const LandingIndex = React.lazy(() => import('../../LandingPage/LandingIndex'))
const AdminHome = React.lazy(() => import('../AdminFrame/AdminHome'))
const AdminDash = React.lazy(() => import('../AdminFrame/AdminDash'))
const Adminchart = React.lazy(() => import('../AdminFrame/Graphs/Adminchart'))
const BasicDetails = React.lazy(() => import('../PatientFrame/ConsultationBook/BasicDetails'))
const SelectDept = React.lazy(() => import('../PatientFrame/ConsultationBook/SelectDept'))
const MyConsults = React.lazy(() => import('../DoctorFrame/MyConsults'))
const ConsultationUpdate = React.lazy(() => import('../DoctorFrame/DocUpdates/Online/UpdatesMain/ConsultationUpdate'))
const Login = React.lazy(() => import('../Auth/Log/Login'))
const SetName = React.lazy(() => import('../Auth/Register/SetName'))
const Pin = React.lazy(() => import('../Auth/Log/Pin'))
const Verification = React.lazy(() => import('../Auth/Register/Verification'))
const ResetPin = React.lazy(() => import('../Auth/Log/ResetPin'))
const LoginWithOtp = React.lazy(() => import('./LoginChild/LoginWithOtp'))
const OutpatientUpdates = React.lazy(() => import('../DoctorFrame/DocUpdates/OutPatient/OutpatientUpdates'))
const KycPending = React.lazy(() => import('../DoctorFrame/KycData/KycPending'))
const SettingsRoot = React.lazy(() => import('../AdminFrame/Settings/SettingsRoot'))
const MainView = React.lazy(() => import('../AdminFrame/Settings/ManageRooms/MainView'))
const InpatientCharts = React.lazy(() => import('../AdminFrame/InpatientCharts/InpatientCharts'))
const DepartmentsPage = React.lazy(() => import('../AdminFrame/Settings/Departments/DepartmentsPage'))
const PharmacyModule = React.lazy(() => import('../AdminFrame/Settings/Pharmacy/PharmacyModule'))
const ConfigureTemplates = React.lazy(() => import('../AdminFrame/Settings/Templates/ConfigureTemplates'))
const DepartmentTemplates = React.lazy(() => import('../AdminFrame/Settings/Templates/DepartmentTemplates'))
const AddNewTemplate = React.lazy(() => import('../AdminFrame/Settings/Templates/AddNewTemplate'))
const ViewTemplate = React.lazy(() => import('../AdminFrame/Settings/Templates/ViewTemplate'))
const KYCPendingList = React.lazy(() => import('../AdminFrame/Settings/KYCPendingList/KYCPendingList'))
const HrHome = React.lazy(() => import('../HrFrame/HrHome'))
const HrDashboard = React.lazy(() => import('../HrFrame/HrDashboard'))
const ManageDoctorsPage = React.lazy(() => import('../HrFrame/ManageDoctors/ManageDoctorsPage'))
const ServicesPage = React.lazy(() => import('../LabFrame/ServicesPage'))
const ServiceDetailsPage = React.lazy(() => import('../LabFrame/ServiceDetailsPage'))
const PackagesPage = React.lazy(() => import('../LabFrame/PackagesPage'))
const PatientTests = React.lazy(() => import('../LabFrame/PatientLabs/PatientTests'))
const TodaysConsults = React.lazy(() => import('../LabFrame/PatientLabs/TodaysConsults'))
const SearchPatinets = React.lazy(() => import('../LabFrame/PatientLabs/SearchPatinets'))
const AllPendingList = React.lazy(() => import('../LabFrame/PatientLabs/AllPendingList'))
const ManageReports = React.lazy(() => import('../DoctorFrame/Reports/ManageReports'))
const BloodBankPage = React.lazy(() => import('../AdminFrame/BloodBank/BloodBankPage'))
const BbRequestForms = React.lazy(() => import('../LabFrame/BbRequestFomrs/BbRequestForms'))
const ComponentSplitter = React.lazy(() => import('../LabFrame/BbRequestFomrs/ComponentSplitter'))
const SurgeryAllocation = React.lazy(() => import('../AdminFrame/SurgeryAllocation/SurgeryAllocation'))
const PatientsDataPage = React.lazy(() => import('../AdminFrame/PatientsDataPage/PatientsDataPage'))
const ConsultationPage = React.lazy(() => import('../../video/Vido'))
const ImagingTechnicianDashboard = React.lazy(() => import('../ImagingTechnician/ImagingTechnicianDashboard'))
const EditTemplate = React.lazy(() => import('../AdminFrame/Settings/Templates/EditTemplate'))
const OrderlyDashboard = React.lazy(() => import('../OrderlyDashboard/OrderlyDashboard'))
const SecretaryDashboard = React.lazy(() => import('../Secretary/SecretaryDashboard'))
const FinancialDashboard = React.lazy(() => import('../Financial/FinancialDashboard'))
const PatientServices = React.lazy(() => import('../DoctorFrame/PatientServices'))
const DoctorInpatients = React.lazy(() => import('../DoctorFrame/DoctorInpatients'))
const NursingTemplates = React.lazy(() => import('../NurseFrame/Templates/NursingTemplates'))
const NursingDepartmentTemplates = React.lazy(() => import('../NurseFrame/Templates/NursingDepartmentTemplates'))
const NursingAddNewTemplate = React.lazy(() => import('../NurseFrame/Templates/NursingAddNewTemplate'))
const NursingViewTemplate = React.lazy(() => import('../NurseFrame/Templates/NursingViewTemplate'))
const Privacy = React.lazy(() => import('../../LandingPage/Body/Privacy'))
const Terms = React.lazy(() => import('../../LandingPage/Body/Terms'))
const Documentation = React.lazy(() => import('../../LandingPage/Body/Documentation'))
const Support = React.lazy(() => import('../../LandingPage/Body/Support'))
const ManageInvoiceTypesPage = React.lazy(() => import('../AdminFrame/Settings/InvoiceTypes/ManageInvoiceTypes'));
const ImagingServicesPage = React.lazy(() => import('../ImagingTechnician/ImagingServicesPage'));
const AdminPatientRegistration = React.lazy(() => import('../AdminFrame/AdminPatientRegistration'))
const EditDoctorProfile = React.lazy(() => import('../AdminFrame/Settings/KYCPendingList/EditDoctorProfile'));

// Pharmacist Module Imports
const PharmacistHome = React.lazy(() => import('../PharmacistFrame/PharmacistHome'));
const PharmacistDashboard = React.lazy(() => import('../PharmacistFrame/PharmacistDashboard'));
const InventoryManagement = React.lazy(() => import('../PharmacistFrame/InventoryManagement'));
const MedicineMasterManagement = React.lazy(() => import('../PharmacistFrame/MedicineMasterManagement'));
const PurchaseManagement = React.lazy(() => import('../PharmacistFrame/PurchaseManagement'));

// Cashier Module Imports
const CashierHome = React.lazy(() => import('../CashierFrame/CashierHome'));
const CashierDashboard = React.lazy(() => import('../CashierFrame/CashierDashboard'));
const ProcessPayment = React.lazy(() => import('../CashierFrame/ProcessPayment'));
const PaymentHistory = React.lazy(() => import('../CashierFrame/PaymentHistory'));
const DailyReport = React.lazy(() => import('../CashierFrame/DailyReport'));

// Admin Invoice Management Import
const AdminInvoiceManagement = React.lazy(() => import('../AdminFrame/AdminInvoiceManagement'));
const ManageInsurance = React.lazy(() => import('../AdminFrame/Settings/Insurance/ManageInsurance'));
const PatientHistory = React.lazy(() => import('../DoctorFrame/PatientHistory'));
const DocumentManagement = React.lazy(() => import('../AdminFrame/DocumentManagement/DocumentManagement'))
const OptometristLayout = React.lazy(() => import('../Optometrist/OptometristLayout'));
const OptometristDashboard = React.lazy(() => import('../Optometrist/OptometristDashboard'));
const OptometristUpdates = React.lazy(() => import('../Optometrist/OptometristUpdates'));
const OptometristTemplates = React.lazy(() => import('../Optometrist/Templates/OptometristTemplates'));
const OptometristDepartmentTemplates = React.lazy(() => import('../Optometrist/Templates/OptometristDepartmentTemplates'));
const OptometristViewTemplate = React.lazy(() => import('../Optometrist/Templates/OptometristViewTemplate'));
const UpcomingConsultationsPage = React.lazy(() => import('../Shared/UpcomingConsultationsPage'));
const OptometristAddNewTemplate = React.lazy(() => import('../Optometrist/Templates/OptometristAddNewTemplate'));
const OptometristEditTemplate = React.lazy(() => import('../Optometrist/Templates/OptometristEditTemplate'));
function AdminMainWrapper() {
  return (
    <Router>
      <AdminMain />
    </Router>
  );
}

function AdminMain() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  
  // Define authentication routes where language switcher should be shown
  const authRoutes = [
    '/login', 
    '/signup',
    '/setname',
    '/pin',
    '/verification',
    '/reset',
    '/loginotp'
  ];
  
  // Keep the floating switcher off the landing page because the landing header owns it.
  const shouldShowLanguageSwitcher = authRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );
  
  // Function to change language
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('preferred-language', lng);
    document.documentElement.lang = lng;
  };
  
  // Handle window resize for responsive positioning
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load preferred language on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Dynamic styles based on device
  const getLanguageSwitcherStyles = () => {
    if (isMobile) {
      return {
        position: 'fixed',
        bottom: isSmallMobile ? '70px' : '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: isSmallMobile ? '8px 16px' : '10px 20px',
        borderRadius: '50px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
        display: 'flex',
        gap: '15px',
        transition: 'all 0.3s ease',
        animation: 'slideUp 0.3s ease-out'
      };
    } else {
      return {
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        backgroundColor: 'white',
        padding: '10px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '10px',
        transition: 'all 0.3s ease'
      };
    }
  };

  const getButtonStyles = (lang) => {
    const isActive = i18n.language === lang;
    const baseStyles = {
      cursor: 'pointer',
      borderRadius: '6px',
      fontWeight: 'bold',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '5px'
    };
    
    if (isMobile) {
      return {
        ...baseStyles,
        padding: isSmallMobile ? '6px 14px' : '8px 18px',
        border: `2px solid ${isActive ? '#007bff' : '#ddd'}`,
        background: isActive ? '#007bff' : 'white',
        color: isActive ? 'white' : 'black',
        fontSize: isSmallMobile ? '12px' : '14px',
        minWidth: isSmallMobile ? '45px' : '60px',
        justifyContent: 'center'
      };
    } else {
      return {
        ...baseStyles,
        padding: '8px 16px',
        border: `2px solid ${isActive ? '#007bff' : '#ddd'}`,
        background: isActive ? '#007bff' : 'white',
        color: isActive ? 'white' : 'black',
        fontSize: '14px',
        minWidth: '60px'
      };
    }
  };

  return (
    <div className="AdminMain">
      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateX(-50%) translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateX(-50%) translateY(0);
              opacity: 1;
            }
          }
          
          .lang-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          }
          
          @media (max-width: 1024px) and (min-width: 769px) {
            .language-switcher-desktop-fixed {
              top: 15px !important;
              right: 15px !important;
              padding: 6px 10px !important;
            }
          }
        `}
      </style>
      
      {/* Conditionally render Language Switcher */}
      {shouldShowLanguageSwitcher && (
        <div style={getLanguageSwitcherStyles()} className={isMobile ? 'language-switcher-mobile' : 'language-switcher-desktop-fixed'}>
          <button 
            onClick={() => changeLanguage('en')}
            style={getButtonStyles('en')}
            className="lang-btn"
          >
            <FaLanguage size={isMobile ? (isSmallMobile ? 10 : 12) : 14} /> EN
          </button>
          <button 
            onClick={() => changeLanguage('fr')}
            style={getButtonStyles('fr')}
            className="lang-btn"
          >
            <FaLanguage size={isMobile ? (isSmallMobile ? 10 : 12) : 14} /> FR
          </button>
        </div>
      )}
      
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Landing Page Routes */}
          <Route path='/' element={<LandingIndex/>}></Route>
          <Route path='/privacy' element={<Privacy />} />
          <Route path='/terms' element={<Terms />} />
          <Route path='/documentation' element={<Documentation />} />
          <Route path='/support' element={<Support />} />
          
          {/* Authentication Routes */}
          <Route path='login' element={<Login/>}></Route>
          <Route path='/signup' element={<Signup/>}></Route>
          <Route path='/setname' element={<SetName/>}></Route>
          <Route path='/pin' element={<Pin/>}></Route>
          <Route path='/verification' element={<Verification/>}></Route>
          <Route path='/reset' element={<ResetPin/>}></Route>
          <Route path='/loginotp' element={<LoginWithOtp/>}></Route>
          
          <Route path="Home/" element={<Home/>}>
            {/* dashboard routes */}
            <Route path='admindashboard' element={<AdminDashboard/>}/>
            <Route path='admindashboard/admindashchart/:id?' element={<AdminDashChart/>}/>
            <Route path='admindashboard/adminresultchart' element={<Adminresultchart/>}/>
            <Route path="admindashboard/dashboard/:id?" element={<Dashboard />} />
            <Route path="dashboard/all" element={<All/>} />
            <Route path="diagnosis/:id" element={<DocDiagnosis/>} /> 
            <Route path="admindashboard/pychomatric" element={<Pychomatric/>}/>
            {/* chartdashboard */}
            <Route path="patientdetailschart/dashboard/:id?" element={<Dashboard />} /> 
            <Route path="labdetailschart/dashboard/:id?" element={<Dashboard />} /> 
            {/* manage inpatient */}
            <Route path='dischargepatient' element={<Discharge/>}></Route>
            {/* Profileroutes */}
            <Route path="profile" element={<Profile/>} />
            <Route path="profile/editprofile" element={<Editprofile/>} />
            {/* Pharmacyroutes */}
            <Route path="pharmacy" element={<Pharmacy/>} />
            <Route path="pharmacy/purchase" element={<Purchase/>} />
            <Route path="pharmacy/sales" element={<Sales/>} />
            <Route path="pharmacy/expired" element={<Expired/>} />
            <Route path="pharmacy/inventory" element={<Inventory/>} />
            <Route path="pharmacy/viewall" element={<Salesviewall/>} />
            <Route path="pharmacy/addmedicine/:id" element={<Addmedicine/>} />
            <Route path="viewdetails/:id" element={<Viewdetails/>} />
            <Route path='pharmacy/addpharmacypatients' element={<AddPharmacyPatients/>}/>
            {/* patientroutes */}
            <Route path='addpateints' element={<Addpatients/>}/>
            <Route path='patientrecord' element={<Patientrecord/>}/>
            <Route path='addusers'  element={<Addusers/>}/>
            <Route path='monitor'  element={<Monitorpatient/>}/>
            {/* labroutes */}
            <Route path='labinvoice' element={<Labinvoice/>}/>
            <Route path='results' element={<Results/>}/>
            <Route path='results/publishresuit' element={<PublishResult/>}/>
            <Route path='results/printresult/:id' element={<PrintResult/>}/>
            <Route path='addservices' element={<Addservices/>}/>
            <Route path='services/addservicesviewall' element={<AddveiwAll/>}/>
            <Route path='addpackages' element={<Addpackages/>}/>
            <Route path='addservicegroup' element={<Addservicegroup/>}/>
            <Route path='addspecimen' element={<Addspecimen/>}/>
            {/* departments */}
            <Route path='addepartment' element={<AddDeparment/>}/>
            {/* doctors */}
            <Route path='adddoctor' element={<AddDoctor/>}/>
            <Route path='addfloor' element={<Addfloor_room/>}/>
            <Route path='admindashboard/availabledoctors' element={<AvailableDoctor/>}/>
            <Route path='kycpending' element={<KycPending/>}></Route>
            <Route path='addtemplate' element={<AddTemplate/>}/>
            {/* blood bank */}
            <Route path='admindashboard/requestdonation' element={<RequestDonation/>}/>
            <Route path='blooddonation' element={<BloodDonation/>}/>
            {/* invoices */}
            <Route path='invoicemain' element={<InvoiceParent/>}/>
            <Route path='invoicemain/invoice' element={<Invoice/>}/>
            <Route path='printinvoice/:invoiceno/:type' element={<Printinvoice/>}/>
            <Route path='invoicemain/searchinvoice' element={<Searchinvoices/>}/>
            <Route path='consultation/:appointmentId' element={<ConsultationPage />} />
          </Route>

          {/* ========================= ADMIN ROUTES ========================= */}
          <Route path='/admin/' element={<AdminHome/>}>
            <Route path='admindash' element={<AdminDash/>} />
              <Route path='upcoming-consultations' element={<UpcomingConsultationsPage />} />

            <Route path='managesettings' element={<SettingsRoot/>}/>
            <Route path='admindash/adminchart' element={<Adminchart/>}/>
            <Route path='admindash/documentmanagement' element={<DocumentManagement />} />
            <Route path="/admin/admindash/bloodbank" element={<BloodBankPage />} />
            <Route path="/admin/admindash/patientdata" element={<PatientsDataPage />} />
            <Route path="/admin/admindash/surgery-allocation" element={<SurgeryAllocation />} />
            <Route path='managesettings/managerooms' element={<MainView/>}/>
            <Route path='managesettings/managedepartment' element={<DepartmentsPage/>}/>
            <Route path='managesettings/managepharmacy' element={<PharmacyModule/>}/>  
            <Route path='managesettings/assignrole' element={<AssignRole/>}/> 
            <Route path='managesettings/managetemplates' element={<ConfigureTemplates/>}/>
            <Route path="managesettings/managetemplates/:departmentId" element={<DepartmentTemplates />} />
            <Route path="managesettings/managetemplates/:departmentId/add" element={<AddNewTemplate />} />
            <Route path="managesettings/managetemplates/:departmentId/view/:templateId" element={<ViewTemplate />} />
            <Route path="managesettings/managetemplates/:departmentId/edit/:templateId" element={<EditTemplate />} />
            <Route path="managesettings/managekyc" element={<KYCPendingList />} />
            <Route path="managesettings/editdoctorprofile" element={<EditDoctorProfile />} />
            <Route path='managesettings/manageinvoicetypes' element={<ManageInvoiceTypesPage/>}/>
            <Route path="register-patient" element={<AdminPatientRegistration />} />
            <Route path='managesettings/manageinsurance' element={<ManageInsurance/>}/>
            <Route path="invoices" element={<AdminInvoiceManagement />} />
          </Route>
          {/* ================================================================ */}

          <Route path='/hr/' element={<HrHome/>}>
            <Route path='hrdashboard' element={<HrDashboard/>}/>
            <Route path='hrdashboard/managedoctors' element={<ManageDoctorsPage/>}/>
          </Route>

          {/* doctor frame */}
          <Route path="/doctor/" element={<DoctorHome/>}>
            <Route path='doctordashboard' element={<Doctordashboard/>}/>
            <Route path='doctordashboard/viewreports' element={<ManageReports/>}/>
            <Route path="doctordashboard/pychomatric" element={<Pychomatric/>}/>
            <Route path="doctordashboard/dashboard/:id?" element={<Dashboard />} />
            <Route path="dashboard/all" element={<All/>} />
            <Route path="patient-history/:mrn?" element={<PatientHistory />} />
  <Route path='doctordashboard/patientcheckout' element={<Discharge/>}/>

            <Route path='addservices' element={<Addservices/>}/>
            <Route path="mypatients" element={<DocMypatients />} />
            <Route path="docinpatient" element={<DocINpatients/>} />
            <Route path='consultedpatients' element={<Consultedpatients/>}/>
            <Route path="diagnosis/:id" element={<DocDiagnosis/>} /> 
            <Route path="profile" element={<Profile/>} />
            <Route path="viewdetails/:id" element={<Viewdetails/>} />
            <Route path="profile/editprofile" element={<Editprofile/>} />
            <Route path='addarticle' element={<AddArticle/>}/>
            <Route path='doctordashboard/requestdonation' element={<RequestDonation/>}/>
            <Route path='vedioconfernce' element={<Vedioconference/>}/>
            <Route path='createroom'  element={<VedioCreateRoom/>}/>
            <Route path='mycalendar' element={<Mycalender/>}/>
            <Route path="myconsultations" element={<MyConsults/>}/>
            <Route path="patientservices" element={<PatientServices/>}/>
            <Route path='onlineupdates/:id' element={<ConsultationUpdate/>}/>
            <Route path='outpatientupdates/:id' element={<OutpatientUpdates/>}></Route>
            <Route path='doctordashboard/inpatientcharts' element={<InpatientCharts/>}/>
            <Route path="doctordashboard/doctorinpatients" element={<DoctorInpatients />} />
          </Route>

          {/* Nurse frame */}
          <Route path='/nurse/' element={<NurseHome/>}>
            <Route path='nursedashboard' element={<NurseDasboard/>}/>
            <Route path="nursedashboard/dashboard" element={<Dashboard />} />
            <Route path="nursedashboard/pychomatric" element={<Pychomatric/>}/>
            <Route path='nursenavpad' element={<NurseNavpad/>}/>
            <Route path='addservices' element={<Addservices/>}/>
            <Route path="profile" element={<Profile/>} />
            <Route path='nursemypatient' element={<NurseMypatients/>}/>
            <Route path='nurseinpatient' element={<NurseInpatient/>}/>
            <Route path="nursingtemplates" element={<NursingTemplates />} />
            <Route path="nursingtemplates/:departmentId" element={<NursingDepartmentTemplates />} />
            <Route path="nursingtemplates/:departmentId/add" element={<NursingAddNewTemplate />} />
            <Route path="nursingtemplates/:departmentId/view/:templateId" element={<NursingViewTemplate />} />
            <Route path='requestdonation' element={<RequestDonation/>}/>
            <Route path='blooddonation' element={<BloodDonation/>}/>
          </Route> 

          {/* patientFrame */}
          <Route path="/patient/" element={<PatientFrameHome/>}>
            <Route path='patientdashboard' element={<PatientDashboard/>}/>
            <Route path="patientbooking/:id?" element={<PatientBooking />} />
            <Route path='booking' element={<BasicDetails/>}/>
            <Route path='department/:type?' element={<SelectDept/>}/>
            <Route path="patientdashboard/dashboard/:id?" element={<Dashboard />} />
            <Route path='patientdashboard/availabledoctors' element={<AvailableDoctor/>}/>
            <Route path="dashboard/all" element={<All/>} />
            <Route path='myschedule' element={<Myschedule/>}/>
            <Route path='addservices' element={<Addservices/>}/>
            <Route path="profile" element={<Profile/>} />
            <Route path="profile/editprofile" element={<Editprofile/>} />
            <Route path='patientdashboard/requestdonation' element={<RequestDonation/>}/>
            <Route path='vedioconfernce' element={<Vedioconference/>}/>
            <Route path='booklab' element={<PatientLabBooking/>}/>
          </Route>

           <Route path="/labhome/" element={<LabHome/>}>
            <Route path='results' element={<Results/>}/>
            <Route path='labdashboard' element={<LabDashboard/>}/>
            {/* Services routes - now pointing to correct components */}
            <Route path='labdashboard/services' element={<ServicesPage/>}/>
            <Route path='labdashboard/services/:id/view' element={<ServiceDetailsPage/>}/>
            <Route path='labdashboard/packages' element={<PackagesPage/>}/>
            <Route path='labdashboard/patienttests' element={<PatientTests/>}/>
            <Route path='labdashboard/patienttests/todaysconsults' element={<TodaysConsults/>}/>
            <Route path='labdashboard/patienttests/search-patients' element={<SearchPatinets/>}/>
            <Route path='labdashboard/patienttests/pending-tests' element={<AllPendingList/>}/>
            <Route path='labdashboard/bloodbankrequests' element={<BbRequestForms/>}/>
            <Route path='labdashboard/bloodbankrequests/componentsplit' element={<ComponentSplitter/>}/>
            <Route path="labdashboard/dashboard/:id?" element={<Dashboard />} />
            <Route path="dashboard/all" element={<All/>} />
            <Route path='results/publishresuit' element={<PublishResult/>}/>
            <Route path='results/printresult/:id' element={<PrintResult/>}/>
            <Route path='addservices' element={<Addservices/>}/>
            <Route path='services/addservicesviewall' element={<AddveiwAll/>}/>
            <Route path='addpackages' element={<Addpackages/>}/>
            <Route path="profile" element={<Profile/>} />
            <Route path="viewdetails/:id" element={<Viewdetails/>} />
            <Route path="profile/editprofile" element={<Editprofile/>} />
            <Route path='labdashboard/requestdonation' element={<RequestDonation/>}/>
            <Route path='addservicegroup' element={<Addservicegroup/>}/>
            <Route path='addspecimen' element={<Addspecimen/>}/>
          </Route>
          
          {/* pharmacy frame */}
          {/* <Route path='/pharmacyhome/' element={<PharmacyHome/>}>
            <Route path="pharmacy" element={<Pharmacy/>} />
            <Route path='pharmacydashboard' element={<PharmacyDashboard/>}/>
            <Route path='pharmacydetails' element={<PharmacyDetails/>}/>
            <Route path="pharmacydashboard/dashboard/:id?" element={<Dashboard />} />
            <Route path="dashboard/all" element={<All/>} />
            <Route path='addservices' element={<Addservices/>}/>
            <Route path="pharmacy/purchase" element={<Purchase/>} />
            <Route path="pharmacy/sales" element={<Sales/>} />
            <Route path="pharmacy/expired" element={<Expired/>} />
            <Route path="pharmacy/inventory" element={<Inventory/>} />
            <Route path="pharmacy/viewall" element={<Salesviewall/>} />
            <Route path="pharmacy/addmedicine/:id" element={<Addmedicine/>} />
            <Route path='pharmacy/addpharmacypatients' element={<AddPharmacyPatients/>}/>
            <Route path="viewdetails/:id" element={<Viewdetails/>} />
            <Route path="profile" element={<Profile/>} />
            <Route path="profile/editprofile" element={<Editprofile/>} />
            <Route path='pharmacydashboard/requestdonation' element={<RequestDonation/>}/>
          </Route> */}

          {/* receptionframe */}
          <Route path='/receptionhome/' element={<ReceptionHome/>}>
            <Route path='receptiondashboard' element={<ReceptionDashboard/>}/>
            <Route path="receptiondashboard/dashboard" element={<Dashboard />} />
            <Route path="dashboard/all" element={<All/>} />
            <Route path='addservices' element={<Addservices/>}/>
            <Route path='addpateints' element={<Addpatients/>}/>
            <Route path='dischargepatient' element={<Discharge/>}></Route>
            <Route path='patientrecord' element={<Patientrecord/>}/>
            <Route path="viewdetails/:id" element={<Viewdetails/>} />
            <Route path='monitor'  element={<Monitorpatient/>}/>
            <Route path='addusers'  element={<Addusers/>}/>
            <Route path="profile" element={<Profile/>} />
            <Route path="profile/editprofile" element={<Editprofile/>} />
            <Route path='receptiondashboard/requestdonation' element={<RequestDonation/>}/>
          </Route>
          
          <Route path="consultation/:appointmentId" element={<ConsultationPage />} />
          <Route path="consultation" element={<ConsultationPage />} />
         
          <Route path='/imaging/'>
            <Route path="/imaging/imagingdashboard" element={<ImagingTechnicianDashboard />} />
            <Route path="/imaging/imaging-services" element={<ImagingServicesPage />} />
          </Route>

          <Route path='/orderly/'>
            <Route path="/orderly/dashboard" element={<OrderlyDashboard />} />
          </Route>
          
          <Route path='secretary'>
            <Route path="/secretary/secretarydashboard" element={<SecretaryDashboard />} />
              <Route path="/secretary/upcoming-consultations" element={<UpcomingConsultationsPage />} />

          </Route>

          <Route path='financial'>
            <Route path="/financial/financialdashboard" element={<FinancialDashboard />} />
          </Route>

          {/* ========================= PHARMACIST ROUTES ========================= */}
          <Route path='/pharmacist/' element={<PharmacistHome/>}>
            <Route index element={<PharmacistDashboard/>} />
            <Route path='pharmacistdashboard' element={<PharmacistDashboard/>}/>
            <Route path='inventory' element={<InventoryManagement/>}/>
            <Route path='medicines' element={<MedicineMasterManagement/>}/>
            <Route path='purchases' element={<PurchaseManagement/>}/>
           
          </Route>
          {/* ===================================================================== */}

          {/* ========================= CASHIER ROUTES ========================= */}
          <Route path='/cashier/' element={<CashierHome/>}>
            <Route index element={<CashierDashboard/>} />
            <Route path='cashierdashboard' element={<CashierDashboard/>}/>
            <Route path='process-payment/:id?' element={<ProcessPayment/>}/>
            <Route path='payment-history' element={<PaymentHistory/>}/>
            <Route path='daily-report' element={<DailyReport/>}/>
          </Route>
{/* ========================= OPTOMETRIST ROUTES ========================= */}
<Route path='/optometrist/' element={<OptometristLayout />}>
  <Route index element={<OptometristDashboard />} />
  <Route path='optometristdashboard' element={<OptometristDashboard />} />
  <Route path='optometristupdates/:id' element={<OptometristUpdates />} />

  {/* Templates */}
  <Route path='templates' element={<OptometristTemplates />} />
  <Route path='templates/:departmentId' element={<OptometristDepartmentTemplates />} />
  <Route path='templates/:departmentId/add' element={<OptometristAddNewTemplate />} />
  <Route path='templates/:departmentId/view/:templateId' element={<OptometristViewTemplate />} />
  <Route path='templates/:departmentId/edit/:templateId' element={<OptometristEditTemplate />} />
</Route>
{/* ===================================================================== */}
{/* ===================================================================== */}
          <Route path='*' element={<Errorhandling/>}/>
        </Routes>
      </Suspense>
    </div>
  );
}

export default AdminMainWrapper;